/**
 * CategoriesPage Component
 * Page for editing classification criteria
 *
 * Requirements: 5.1, 5.4, 5.5
 *
 * Criteria definitions are now driven by the backend (global criteria +
 * per-user overrides). The frontend no longer stores a hardcoded
 * PREDEFINED_CRITERIA list.
 */

import { useState, useEffect } from 'react';
import './CategoriesPage.css';
import { Skeleton } from '../components/SkeletonScreen';
import { CriteriaService } from '../services/CriteriaService';
import { CustomCriterionModal } from '../components/CustomCriterionModal';
import { useTutorial } from '../components/TutorialProvider';
import type { Criterion } from '../types';

function CategoriesPage() {
  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [originalCriteria, setOriginalCriteria] = useState<Criterion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [criteriaService] = useState(() => new CriteriaService());
  const [selectedCriterionIndex, setSelectedCriterionIndex] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTier, setCurrentTier] = useState<any>(null);
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [editingCustomCriterion, setEditingCustomCriterion] = useState<Criterion | null>(null);

  // Tutorial integration
  const { startTutorial } = useTutorial();

  // Check if there are unsaved changes
  const hasChanges = JSON.stringify(criteria) !== JSON.stringify(originalCriteria);

  // Fetch criteria and subscription on page load (Requirement 5.1)
  useEffect(() => {
    loadCriteria();
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const { SubscriptionService } = await import('../services/SubscriptionService');
      const subscriptionService = new SubscriptionService();
      const [subscriptionData, tiersData] = await Promise.all([
        subscriptionService.getSubscription(),
        subscriptionService.getTiers(),
      ]);

      // Find current tier config - handle both "free" and "Free Tier" naming
      const subscriptionType = subscriptionData.subscription_type.toLowerCase();
      const tier = tiersData.find(t => {
        const tierName = t.name.toLowerCase();
        return tierName === subscriptionType ||
               (subscriptionType === 'free' && tierName === 'free tier') ||
               (subscriptionType === 'free tier' && tierName === 'free');
      });

      if (!tier) {
        console.error('Could not find tier config for:', subscriptionData.subscription_type);
        console.log('Available tiers:', tiersData.map(t => t.name));
      }

      setCurrentTier(tier);
    } catch (err) {
      console.error('Error loading subscription:', err);
    }
  };

  const loadCriteria = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch all criteria (active + inactive) from backend.
      // The backend assembles global definitions + user overrides.
      const fetchedCriteria = await criteriaService.getCriteria(true);

      // Tag each criterion with isPredefined / isCustom based on ID prefix
      const allCriteria = fetchedCriteria.map(c => ({
        ...c,
        isCustom: c.id.startsWith('custom-'),
        isPredefined: !c.id.startsWith('custom-'),
      }));

      setCriteria(allCriteria);
      setOriginalCriteria(allCriteria);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load criteria';
      setError(errorMessage);
      console.error('Error loading criteria:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleCriterion = (index: number) => {
    const updatedCriteria = [...criteria];
    const willBeActive = !updatedCriteria[index].active;

    // Clear any existing errors when deactivating
    if (!willBeActive) {
      setError(null);
    }

    // Check if enabling would exceed limit
    if (willBeActive && currentTier) {
      const currentActiveCount = criteria.filter(c => c.active).length;
      if (currentActiveCount >= currentTier.max_active_criteria) {
        setError(`Your ${currentTier.name} plan allows up to ${currentTier.max_active_criteria} active criteria. Please deactivate another criterion first, or upgrade your plan to activate more.`);
        // Scroll to top to show error
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    updatedCriteria[index] = {
      ...updatedCriteria[index],
      active: willBeActive,
    };
    setCriteria(updatedCriteria);
    if (successMessage) setSuccessMessage(null);
    if (error) setError(null); // Clear error on successful toggle
  };

  const handleOpenModal = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCriterionIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCriterionIndex(null);
  };

  const handleCategoryPriceChange = (categoryIndex: number, field: 'price_min' | 'price_max', value: number) => {
    if (selectedCriterionIndex === null) return;

    const updatedCriteria = [...criteria];
    const updatedCategories = [...updatedCriteria[selectedCriterionIndex].categories];
    updatedCategories[categoryIndex] = {
      ...updatedCategories[categoryIndex],
      [field]: value,
    };
    updatedCriteria[selectedCriterionIndex] = {
      ...updatedCriteria[selectedCriterionIndex],
      categories: updatedCategories,
    };
    setCriteria(updatedCriteria);
    if (successMessage) setSuccessMessage(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Send the full criteria list (active + inactive) to the backend.
      // The backend extracts overrides by diffing against global criteria.
      const allCriteriaToSave = criteria.filter(c =>
        c.categories && c.categories.length >= 1
      );

      // Validate that we have at least one active criterion with valid categories
      const validActive = allCriteriaToSave.filter(c =>
        c.active && c.categories.length >= 2
      );
      if (validActive.length === 0) {
        setError('At least one criterion must be active with valid categories');
        setIsSaving(false);
        return;
      }

      // Requirement 5.4: Send updated criteria to backend
      const savedCriteria = await criteriaService.updateCriteria(allCriteriaToSave);

      // Tag with isPredefined / isCustom
      const allCriteria = savedCriteria.map(c => ({
        ...c,
        isCustom: c.id.startsWith('custom-'),
        isPredefined: !c.id.startsWith('custom-'),
      }));

      // Requirement 5.5: Display success message and update local state
      setCriteria(allCriteria);
      setOriginalCriteria(allCriteria);
      setSuccessMessage('Criteria saved successfully!');

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save criteria';
      setError(errorMessage);
      console.error('Error saving criteria:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setCriteria(originalCriteria);
    setError(null);
    setSuccessMessage(null);
  };

  const handleCreateCustomCriterion = () => {
    if (!currentTier?.custom_criteria_enabled) {
      setError('Custom criteria are only available on Platinum and Enterprise plans. Please upgrade to access this feature.');
      return;
    }

    const customCount = criteria.filter(c => c.isCustom).length;
    if (customCount >= currentTier.max_custom_criteria) {
      setError(`Your ${currentTier.name} plan allows up to ${currentTier.max_custom_criteria} custom criteria. Please remove an existing custom criterion or upgrade your plan.`);
      return;
    }

    setEditingCustomCriterion(null);
    setIsCustomModalOpen(true);
  };

  const handleEditCustomCriterion = (criterion: Criterion, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingCustomCriterion(criterion);
    setIsCustomModalOpen(true);
  };

  const handleSaveCustomCriterion = (criterion: Criterion) => {
    const existingIndex = criteria.findIndex(c => c.id === criterion.id);

    if (existingIndex >= 0) {
      // Update existing custom criterion
      const updatedCriteria = [...criteria];
      updatedCriteria[existingIndex] = criterion;
      setCriteria(updatedCriteria);
    } else {
      // Add new custom criterion
      setCriteria([...criteria, criterion]);
    }

    setSuccessMessage(null);
    setError(null);
  };

  const handleRemoveCustomCriterion = (criterionId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (window.confirm('Are you sure you want to remove this custom criterion? This action cannot be undone.')) {
      const updatedCriteria = criteria.filter(c => c.id !== criterionId);
      setCriteria(updatedCriteria);
      setSuccessMessage(null);
      setError(null);
    }
  };

  if (isLoading) {
    return (
      <div className="categories-page">
        <div className="categories-dashboard">
          <div className="dashboard-header">
            <Skeleton variant="text" width="60%" height={32} />
          </div>
          <div className="criteria-grid">
            {Array.from({ length: 10 }).map((_, index) => (
              <Skeleton key={index} variant="rectangular" width="100%" height={60} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const activeCriteriaCount = criteria.filter(c => c.active).length;
  const validActiveCriteria = criteria.filter(c =>
    c.active && c.categories && c.categories.length >= 2
  ).length;
  const customCriteriaCount = criteria.filter(c => c.isCustom).length;
  const existingCriteriaNames = criteria.map(c => c.name);

  return (
    <div className="categories-page">
      <div className="categories-dashboard">
        <div className="dashboard-header">
          <div className="header-content">
            <div className="title-with-tutorial">
              <h1>Classification Criteria Dashboard</h1>
              <button
                className="tutorial-trigger-btn"
                onClick={() => startTutorial('categories')}
                title="Start Categories Tutorial"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                Tutorial
              </button>
            </div>
            <div className="criteria-stats">
              <span className="stat-badge active">{activeCriteriaCount} Active</span>
              <span className="stat-badge total">{criteria.length} Total</span>
              {customCriteriaCount > 0 && (
                <span className="stat-badge custom">{customCriteriaCount} Custom</span>
              )}
            </div>
          </div>

          {currentTier && (
            <div className={`subscription-banner ${activeCriteriaCount >= currentTier.max_active_criteria ? 'at-limit' : ''}`}>
              <div className="subscription-banner-content">
                <div className="subscription-banner-info">
                  <span className="subscription-banner-label">Your Plan:</span>
                  <span className="subscription-banner-value">{currentTier.name}</span>
                </div>
                <div className="subscription-banner-info">
                  <span className="subscription-banner-label">Active Criteria:</span>
                  <span className={`subscription-banner-value ${activeCriteriaCount >= currentTier.max_active_criteria ? 'limit-reached' : ''}`}>
                    {activeCriteriaCount} / {currentTier.max_active_criteria}
                  </span>
                  {activeCriteriaCount >= currentTier.max_active_criteria && (
                    <span className="limit-warning">Warning: Limit reached</span>
                  )}
                </div>
                {currentTier.custom_criteria_enabled ? (
                  <div className="subscription-banner-info subscription-banner-highlight">
                    <span className="subscription-banner-text">
                      Custom criteria: {customCriteriaCount}/{currentTier.max_custom_criteria} used
                    </span>
                  </div>
                ) : (
                  <div className="subscription-banner-info subscription-banner-upgrade">
                    <span className="subscription-banner-text">
                      Upgrade to Platinum or Enterprise for custom criteria
                    </span>
                  </div>
                )}

                {/* Actions - moved from bottom */}
                <div className="subscription-banner-actions">
                  <div className="status-info">
                    {activeCriteriaCount > 0 && validActiveCriteria === 0 && (
                      <span className="status-warning">Warning: No valid criteria to save</span>
                    )}
                    {hasChanges && (
                      <span className="unsaved-indicator">Unsaved changes</span>
                    )}
                  </div>

                  <div className="action-buttons">
                    <button
                      className="save-btn"
                      onClick={handleSave}
                      disabled={!hasChanges || isSaving || validActiveCriteria === 0}
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>

                    {hasChanges && (
                      <button className="cancel-btn" onClick={handleCancel} disabled={isSaving}>
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message" role="alert">
              <span className="error-icon">!</span>
              <span>{error}</span>
              <button className="dismiss-btn" onClick={() => setError(null)}>X</button>
            </div>
          )}

          {successMessage && (
            <div className="success-message" role="status">
              <span className="success-icon">OK</span>
              <span>{successMessage}</span>
              <button className="dismiss-btn" onClick={() => setSuccessMessage(null)}>X</button>
            </div>
          )}
        </div>

        <div className="criteria-grid">
          {criteria.map((criterion, index) => {
            const hasValidCategories = criterion.categories && criterion.categories.length >= 2;
            const showWarning = criterion.active && !hasValidCategories;

            return (
              <div
                key={criterion.id}
                className={`criterion-card ${criterion.active ? 'active' : 'inactive'} ${showWarning ? 'warning' : ''} ${criterion.isCustom ? 'custom' : ''}`}
                onClick={(e) => criterion.isCustom ? handleEditCustomCriterion(criterion, e) : handleOpenModal(index, e)}
              >
                <div className="criterion-card-header">
                  <div className="criterion-info">
                    <h3>{criterion.name}</h3>
                    {criterion.isCustom && (
                      <>
                        <span className="custom-badge">CUSTOM</span>
                        <button
                          className="remove-custom-btn-icon"
                          onClick={(e) => handleRemoveCustomCriterion(criterion.id, e)}
                          aria-label="Remove custom criterion"
                          title="Remove this custom criterion"
                        >
                          X
                        </button>
                      </>
                    )}
                  </div>
                  <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={criterion.active}
                      onChange={() => handleToggleCriterion(index)}
                      aria-label={`Toggle ${criterion.name}`}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <p className="criterion-description">{criterion.description}</p>
                {showWarning && (
                  <div className="category-warning">
                    Needs at least 2 categories
                  </div>
                )}
                {hasValidCategories && (
                  <div className="category-count">
                    {criterion.categories.length} categories
                  </div>
                )}
              </div>
            );
          })}

          {currentTier?.custom_criteria_enabled && customCriteriaCount < currentTier.max_custom_criteria && (
            <div className="criterion-card add-custom-card" onClick={handleCreateCustomCriterion}>
              <div className="add-custom-content">
                <h3>+ Create Custom Criterion</h3>
                <p className="criterion-description">
                  Define your own classification criteria with custom categories
                </p>
              </div>
            </div>
          )}
        </div>

        <CustomCriterionModal
          isOpen={isCustomModalOpen}
          onClose={() => setIsCustomModalOpen(false)}
          onSave={handleSaveCustomCriterion}
          existingCriterion={editingCustomCriterion}
          maxCategories={currentTier?.max_categories_per_custom || 10}
          existingCriteriaNames={existingCriteriaNames}
        />

        {isModalOpen && selectedCriterionIndex !== null && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{criteria[selectedCriterionIndex].name}</h2>
                <button className="modal-close-btn" onClick={handleCloseModal}>X</button>
              </div>
              <p className="modal-description">{criteria[selectedCriterionIndex].description}</p>

              <div className="categories-grid-modal">
                {criteria[selectedCriterionIndex].categories.map((category, catIndex) => (
                  <div key={catIndex} className="category-card-compact">
                    <h4 className="category-name-compact">{category.name}</h4>
                    <p className="category-desc-compact">{category.description}</p>

                    <div className="price-range-slider">
                      <div className="price-range-labels">
                        <span className="price-label">${category.price_min}</span>
                        <span className="price-label">${category.price_max}</span>
                      </div>
                      <div className="slider-container">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="1"
                          value={category.price_min}
                          onChange={(e) => {
                            const newMin = parseFloat(e.target.value);
                            if (newMin <= category.price_max) {
                              handleCategoryPriceChange(catIndex, 'price_min', newMin);
                            }
                          }}
                          className={`range-slider range-slider-min ${category.price_min === 0 && category.price_max === 0 ? 'slider-hidden' : ''}`}
                          style={{ zIndex: category.price_min === category.price_max ? 2 : 3 }}
                        />
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="1"
                          value={category.price_max}
                          onChange={(e) => {
                            const newMax = parseFloat(e.target.value);
                            if (newMax >= category.price_min) {
                              handleCategoryPriceChange(catIndex, 'price_max', newMax);
                            }
                          }}
                          className={`range-slider range-slider-max`}
                          style={{ zIndex: category.price_min === category.price_max ? 3 : 2 }}
                        />
                        <div className="slider-track">
                          <div
                            className="slider-range"
                            style={{
                              left: `${category.price_min}%`,
                              width: `${category.price_max - category.price_min}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {category.price_min > category.price_max && (
                      <div className="price-error-compact">
                        Invalid range
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="modal-footer">
                <button className="modal-done-btn" onClick={handleCloseModal}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
}

export default CategoriesPage;
